import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { Offices } from 'constants/enums'
import { toast } from 'react-toastify'
import {
  fetchAnalysisReport,
  fetchPendingAnalysisReport,
  getAnalysisMaterials,
  getAnalysisTotalizer,
  getFilterQuotas,
  getRejectMotiveList,
  uploadAnalysisFile,
} from 'services/analysis'
import type { AnalysisInconsistency } from 'services/analysis/interfaces'
import type { AppThunk } from 'store/store'

import type {
  ExtraMaterial,
  ExtraTotalizer,
  ExtraVolumeRejectionMotives,
  Pagination,
  QuotaMonthType,
  RequestAnalysisMaterials,
  RequestAnalysisReport,
  RequestAnalysisTotalizer,
} from './Types/ExtraVolumeType'

type InitialState = {
  loadingExtraVolume: boolean
  loadingReport: boolean
  loadingUpload: boolean
  successUpload: boolean
  uploadErrors: AnalysisInconsistency[]
  materialsList: ExtraMaterial[]
  filterQuotas: QuotaMonthType[]
  rejectionMotives: ExtraVolumeRejectionMotives[]
  totalizer: ExtraTotalizer
  pagination: Pagination
  lastUpdate: string
}
const initialState: InitialState = {
  filterQuotas: [],
  lastUpdate: '',
  loadingExtraVolume: true,
  loadingReport: false,
  loadingUpload: false,
  materialsList: [],
  pagination: {
    currentPage: 1,
    pageSize: 20,
    totalItems: 0,
    totalPages: 0,
  },
  rejectionMotives: [],
  successUpload: false,
  totalizer: {} as ExtraTotalizer,
  uploadErrors: [],
}

type OfficesKeys = keyof typeof Offices

const extraVolume = createSlice({
  initialState,
  name: 'extraVolume',
  reducers: {
    afterRequest(state) {
      state.loadingExtraVolume = false
    },
    onRequest(state) {
      state.loadingExtraVolume = true
    },
    resetPagination(state) {
      state.pagination = {
        currentPage: 1,
        pageSize: 20,
        totalItems: 0,
        totalPages: 0,
      }
    },
    setCurrentPage(state, action: PayloadAction<number>) {
      state.pagination.currentPage = action.payload
    },
    setFilterQuotas(state, action: PayloadAction<QuotaMonthType[]>) {
      state.filterQuotas = action.payload
    },
    setLastUpdate(state, action: PayloadAction<string>) {
      state.lastUpdate = action.payload
    },
    setLoadingReport(state, action: PayloadAction<boolean>) {
      state.loadingReport = action.payload
    },
    setLoadingUpload(state, action: PayloadAction<boolean>) {
      state.loadingUpload = action.payload
    },
    setMaterialList(state, action: PayloadAction<ExtraMaterial[] | undefined>) {
      if (action.payload) {
        state.materialsList = action.payload
      } else {
        state.materialsList = []
      }
    },
    setMotives(state, action: PayloadAction<ExtraVolumeRejectionMotives[]>) {
      state.rejectionMotives = action.payload
    },
    setPagination(state, action: PayloadAction<Pagination>) {
      state.pagination = action.payload
    },
    setSuccessUpload(state, action: PayloadAction<boolean>) {
      state.successUpload = action.payload
    },
    setTotalizer(state, action: PayloadAction<ExtraTotalizer>) {
      state.totalizer = action.payload
    },
    setUploadErrors(state, action: PayloadAction<AnalysisInconsistency[]>) {
      state.uploadErrors = action.payload
    },
  },
})

export const extraVolumeContext = extraVolume.reducer
export const {
  onRequest,
  afterRequest,
  setMaterialList,
  setFilterQuotas,
  setTotalizer,
  setLoadingReport,
  setLoadingUpload,
  setSuccessUpload,
  setUploadErrors,
  setPagination,
  setLastUpdate,
  setCurrentPage,
  resetPagination,
  setMotives,
} = extraVolume.actions

export const asyncGetRejectMotiveList = (): AppThunk => async (dispatch) => {
  try {
    const motives = await getRejectMotiveList()
    dispatch(setMotives(motives))
  } catch (error) {
    toast.error('Erro ao obter motivos de reprovação.')
  }
}

export const asyncGetExtraVolumeData =
  (payload: RequestAnalysisMaterials, loadTotalizer = true): AppThunk =>
  async (dispatch, getState) => {
    dispatch(onRequest())
    const { pagination } = getState().extraVolume

    try {
      delete payload.Materiais
      const {
        TipoCritica,
        Materias,
        MesesCota,
        EscritorioVenda,
        RegiaoVenda,
        Mercado,
        EquipeVenda,
      } = payload

      if (loadTotalizer) {
        const totalizer = await getAnalysisTotalizer({
          EquipeVenda,
          EscritorioVenda,
          Materias,
          Mercado,
          MesesCota,
          RegiaoVenda,
          TipoCritica,
        })
        dispatch(setTotalizer(totalizer))
      }
      const materialsData = await getAnalysisMaterials({ ...payload, MesesCota })
      if (materialsData.length > 0) {
        dispatch(
          setPagination({
            ...pagination,
            currentPage: payload.numeroPagina ? payload.numeroPagina + 1 : 1,
            totalItems: materialsData[0].TotalRegistros,
            totalPages: Math.ceil(materialsData[0].TotalRegistros / pagination.pageSize),
          }),
        )
        dispatch(setMaterialList(materialsData))
      } else {
        dispatch(setMaterialList([]))
        dispatch(
          setPagination({
            ...pagination,
            currentPage: payload.numeroPagina ? payload.numeroPagina + 1 : 1,
            totalItems: 0,
            totalPages: 0,
          }),
        )
      }
    } catch (error) {
      toast.error('Erro ao obter dados de estoque.')
    } finally {
      dispatch(afterRequest())
    }
  }

export const getAnalysisReport =
  (payload: RequestAnalysisReport): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingReport(true))
    toast.info('O download irá iniciar em instantes.')

    try {
      const report = await fetchAnalysisReport(payload)
      const url = URL.createObjectURL(new Blob([report]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'historico_volume_extra.xlsx')
      document.body.appendChild(link)
      link.click()
      link.remove()
      toast.success('Relatório baixado com sucesso!')
    } catch {
      toast.error('Erro ao obter relatório. Por favor, tente novamente em instantes.')
    } finally {
      dispatch(setLoadingReport(false))
    }
  }

export const getPendingAnalysisReport =
  (payload: RequestAnalysisTotalizer): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingReport(true))
    toast.info('O download irá começará em instantes.')

    try {
      const report = await fetchPendingAnalysisReport(payload)
      const url = URL.createObjectURL(new Blob([report]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'volume_extra_pendente.xlsx')
      document.body.appendChild(link)
      link.click()
      link.remove()
      toast.success('Relatório baixado com sucesso!')
    } catch {
      toast.error(
        'Algo deu errado! Tente novamente mais tarde ou entre em contato com o time de Backoffice.',
      )
    } finally {
      dispatch(setLoadingReport(false))
    }
  }

export const getAnalysisQuotas =
  (offices: OfficesKeys[]): AppThunk =>
  async (dispatch) => {
    try {
      const quotas = await getFilterQuotas(offices.length ? offices : [])
      dispatch(setFilterQuotas(quotas.length ? quotas : []))
    } catch (error) {
      toast.error('Erro ao obter meses cota.')
    }
  }

export const sendAnalysisUpload =
  (file: File): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingUpload(true))
    const form = new FormData()
    form.append('arquivo', file)

    uploadAnalysisFile(form)
      .then((data) => {
        if (data.OperacaoRealizada) {
          dispatch(setSuccessUpload(true))
        }
      })
      .catch((error) => {
        toast.error(
          error.response?.data.MensagemRetorno ??
            'Algo deu errado! Tente novamente mais tarde ou entre em contato com o time de Backoffice.',
        )
        if (error.response?.data.Inconsistencias?.length) {
          dispatch(setUploadErrors(error.response.data.Inconsistencias))
        }
      })
      .finally(() => {
        dispatch(setLoadingUpload(false))
      })
  }
